/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  LineElement,
  Legend,
  BarElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import CustomSelect from "~components/Form/components/Select";
import useSelect from "~components/Form/hooks/useSelect";
import BarChart from "../../RouteOptimizationV2/components/Graficos/BarChart";
import LineChart from "../../RouteOptimizationV2/components/Graficos/LineChart";
import GraficoClienteLegenda from "../../RouteOptimizationV2/components/Graficos/GraficoClienteLegenda";
import {
  createColorAssigner,
  getGrupoMaterialColor,
} from "../../RouteOptimizationV2/components/Graficos/chartColors";
import { SelectArrow } from "./FilterSelect";
import { mesParaData } from "../utils/formatters";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartDataLabels,
  Legend,
  Tooltip
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="flex flex-1 flex-col gap-1">
    <label className="mb-1 block text-xs font-semibold text-[#1A2B49]">{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-11 w-full appearance-none rounded-md border border-[#C8CED8] bg-white px-3 pr-10 text-[15px] font-semibold text-[#1A2B49] outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <SelectArrow />
    </div>
  </div>
);

const FiltroGrafico = ({ filtros, filters, setFilters, cleanFilters }) => {
  const grupos = filtros?.gruposMateriais ?? [];
  const todosProdutos = filtros?.produtos ?? [];

  const grupoOptions = useMemo(
    () => [{ value: 0, label: "Todos" }, ...grupos.map((g) => ({ value: g.id, label: g.nome }))],
    [grupos]
  );

  const produtoOptions = useMemo(() => {
    const lista = filters.grupoMaterialId
      ? todosProdutos.filter((p) => p.grupoMaterialId === filters.grupoMaterialId)
      : todosProdutos;
    return [{ value: 0, label: "Todos" }, ...lista.map((p) => ({ value: p.id, label: p.nome }))];
  }, [todosProdutos, filters.grupoMaterialId]);

  const handleGrupoChange = (val) => {
    setFilters({ ...filters, grupoMaterialId: val, produtoId: 0 });
  };

  const handleProdutoChange = (val) => {
    setFilters({ ...filters, produtoId: val });
  };

  return (
    <div className="mb-8 flex flex-wrap items-end gap-4">
      <FilterSelect
        label="Grupo de Produtos:"
        value={filters.grupoMaterialId ?? 0}
        onChange={handleGrupoChange}
        options={grupoOptions}
      />
      <FilterSelect
        label="Produtos:"
        value={filters.produtoId ?? 0}
        onChange={handleProdutoChange}
        options={produtoOptions}
      />
      {(filters.grupoMaterialId || filters.produtoId) ? (
        <button
          type="button"
          onClick={cleanFilters}
          className="h-11 shrink-0 rounded-md border border-[#C8CED8] bg-white px-4 text-sm font-semibold text-[#1A2B49] hover:bg-gray-50"
        >
          Limpar filtros
        </button>
      ) : null}
    </div>
  );
};


const temMovimento = (item) =>
  (item.mes ?? []).some((m) => m.volume !== 0 || m.volumeDtu !== 0 || m.faturamentoBruto !== 0);

const GraficoClienteStatic = ({
  faturamentos,
  faturamentoLastCreatedAtDate,
  filtros,
  govs,
  filters,
  cleanFilters,
  setFilters,
  mes,
}) => {
  const faturamento = useSelect("faturamento");
  const volume = useSelect("volume");

  useLayoutEffect(() => {
    faturamento.upDateDefaultValue({ label: "Faturamento (R$)", value: "revenues" });
    volume.upDateDefaultValue({ label: "Volume (t)", value: "volumes" });
    faturamento.upDateValue("revenues");
    volume.upDateValue("volumes");
  }, []);

  const datas = useMemo(() => {
    const fmt = (date) => {
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = String(date.getFullYear()).slice(-2);
      return `${m}/${y}`;
    };
    const base = mesParaData(mes) ?? new Date();
    const months = [fmt(new Date(base.getFullYear() - 1, base.getMonth()))];
    for (let i = 4; i > 0; i--) months.push(fmt(new Date(base.getFullYear(), base.getMonth() - i)));
    months.push(fmt(base));
    return months;
  }, [mes]);

  const clientes = useMemo(() => {
    const map = new Map();
    for (const obj of faturamentos || []) {
      map.set(obj.clienteId, { id: obj.clienteId, codigo: obj.codigoCliente, nome: obj.nomeCliente });
    }
    return Array.from(map.values());
  }, [faturamentos]);

  const grupos = useMemo(() => {
    const map = new Map();
    for (const obj of faturamentos || []) {
      map.set(obj.grupoMaterialId, { id: obj.grupoMaterialId, nome: obj.nomeGrupoMaterial });
    }
    return Array.from(map.values());
  }, [faturamentos]);

  const produtos = useMemo(() => {
    const map = new Map();
    for (const obj of faturamentos || []) {
      map.set(obj.produtoId, {
        id: obj.produtoId,
        grupoMaterialId: obj.grupoMaterialId,
        codigo: obj.codigoProduto,
        nome: obj.nomeProduto,
      });
    }
    return Array.from(map.values());
  }, [faturamentos]);

  const newFaturamento = useMemo(
    () =>
      (faturamentos || []).map((fat) => ({
        ...fat,
        newData: new Date(fat.data).toLocaleString("pt-BR", { year: "2-digit", month: "2-digit" }),
      })),
    [faturamentos]
  );

  const sumIndicador = useCallback((fats, indicador, fob = false) => {
    const sumValues = (items, key) =>
      items.reduce((soma, item) => {
        const val = item[key];
        const num = typeof val === "string" ? Number.parseFloat(val.replace(",", ".")) : val;
        return soma + (Number.isNaN(num) ? 0 : num);
      }, 0);
    const s = sumValues(fats, indicador);
    if (!fob) return s;
    const sv = sumValues(fats, "volume");
    return sv === 0 ? 0 : s / sv;
  }, []);

  const buildMes = (fats, data) => ({
    mes: data,
    volume: sumIndicador(fats, "volume"),
    volumeDtu: sumIndicador(fats, "volumeDu"),
    faturamentoBruto: sumIndicador(fats, "faturamentoBruto"),
    precoFobLiquido: sumIndicador(fats, "precoFobLiquidoTotal", true),
  });

  const createClienteData = (cliente) => {
    if (filters.grupoMaterialId) {

      const filteredProdutos = filters.produtoId
        ? produtos.filter((p) => p.id === filters.produtoId)
        : produtos.filter((p) => p.grupoMaterialId === filters.grupoMaterialId);
      return filteredProdutos.map((item) => ({
        cliente: cliente.nome,
        label: item.nome,
        id: item.id,
        mes: datas.map((data) => {
          const fats = newFaturamento.filter((f) => f.produtoId === item.id && f.newData === data);
          return buildMes(fats, data);
        }),
      }));
    }

    if (filters.produtoId) {
      const filteredProdutos = produtos.filter((p) => p.id === filters.produtoId);
      return filteredProdutos.map((item) => ({
        cliente: cliente.nome,
        label: item.nome,
        id: item.id,
        mes: datas.map((data) => {
          const fats = newFaturamento.filter((f) => f.produtoId === item.id && f.newData === data);
          return buildMes(fats, data);
        }),
      }));
    }

    return grupos.map((item) => ({
      cliente: cliente.nome,
      label: item.nome,
      id: item.id,
      mes: datas.map((data) => {
        const fats = newFaturamento.filter((f) => f.grupoMaterialId === item.id && f.newData === data);
        return buildMes(fats, data);
      }),
    }));
  };

  const dados = useMemo(
    () => clientes.flatMap(createClienteData).filter(temMovimento),
    [clientes, produtos, grupos, newFaturamento, datas, filters.grupoMaterialId, filters.produtoId]
  );

  const coresPorLabel = useMemo(() => {
    const assignColor = createColorAssigner();
    const mapa = new Map();
    for (const d of dados) {
      if (mapa.has(d.label)) continue;
      mapa.set(d.label, getGrupoMaterialColor(d.label) ?? assignColor(d.label));
    }
    return mapa;
  }, [dados]);

  const getData = (item, col) =>
    datas.map((label) => {
      const m = item.mes.find((d) => d.mes === label);
      return m ? m[col] : 0;
    });

  const getColors = (item) =>
    datas.map((label) => {
      const m = item.mes.find((d) => d.mes === label);
      return m?.cor || "#034EA2";
    });

  const govsVolume = useMemo(() => {
    if (!govs?.length || filters.produtoId) return null;

    const metaDoGrupo = (grupoId) => govs.find((g) => g.grupoMaterialId === grupoId)?.metaGov ?? 0;
    const toGov = (id, name, meta) => ({ id, name, volumes: meta, volumesDtus: meta });

    if (filters.grupoMaterialId) {
      const meta = metaDoGrupo(filters.grupoMaterialId);
      const primeiro = dados[0];
      if (!meta || !primeiro) return null;
      return [toGov(primeiro.id, primeiro.label, meta)];
    }

    const lista = dados
      .filter((d) => metaDoGrupo(d.id) > 0)
      .map((d) => toGov(d.id, d.label, metaDoGrupo(d.id)));
    return lista.length ? lista : null;
  }, [govs, dados, filters.grupoMaterialId, filters.produtoId]);

  const dataVolume = useMemo(() => ({
    labels: datas,
    groups: dados.map((d) => ({
      name: d.label,
      id: d.id,
      chartColor: coresPorLabel.get(d.label),
      volumes: getData(d, "volume"),
      volumesDtus: getData(d, "volumeDtu"),
      colors: getColors(d),
      filtro: filters.grupoMaterialId ? "produto" : "grupo",
    })),
    govs: govsVolume,
  }), [dados, datas, govsVolume, coresPorLabel]);

  const dataFaturamento = useMemo(() => ({
    labels: datas,
    groups: dados.map((d) => ({
      name: d.label,
      id: d.id,
      chartColor: coresPorLabel.get(d.label),
      revenues: getData(d, "faturamentoBruto"),
      liquidos: getData(d, "precoFobLiquido"),
      filtro: filters.grupoMaterialId ? "produto" : "grupo",
    })),
    govs: (govs || []).map((g) => ({
      name: g.nomeGrupoMaterial,
      id: g.grupoMaterialId,
      revenues: g?.metaFob ?? 0,
      liquidos: g?.metaFob ?? 0,
    })),
  }), [dados, datas, govs, coresPorLabel]);

  const calculaSoma = (arr, opcao) =>
    arr.reduce((acc, val) => acc + (val[opcao]?.reduce ? val[opcao].slice(1).reduce((s, v) => s + v, 0) : val[opcao] || 0), 0);

  const getMaxPositiveCount = (arr, opcao) =>
    Math.max(...arr.map((p) => (Array.isArray(p[opcao]) ? p[opcao].slice(1).filter((n) => n > 0).length : 0)));

  const calculaTotal = (groups, opcao) => {
    if (!groups.length) return 0;
    const soma = calculaSoma(groups, opcao);
    const max = getMaxPositiveCount(groups, opcao);
    return max === 0 ? 0 : soma / max;
  };

  const totalVolume = useMemo(
    () => calculaTotal(dataVolume.groups, volume.initValue),
    [dataVolume.groups, volume.initValue]
  );

  const totalFaturamento = useMemo(
    () => calculaTotal(dataFaturamento.groups, faturamento.initValue),
    [dataFaturamento.groups, faturamento.initValue]
  );

  return (
    <div className="containerForm container bg-white p-7 rounded-lg mt-[28px] relative">
      <FiltroGrafico
        filtros={filtros}
        filters={filters}
        setFilters={setFilters}
        cleanFilters={cleanFilters}
      />
      <div className="container-grafico">
        <div>
          <h4 className="text-lg text-primaria font-bold h-[50px] max-w-[400px] mx-auto">
            <CustomSelect
              placeholder="Volume"
              name="volume"
              className="select-filter focus:outline-none focus:border-none focus:shadow-none"
              options={[
                { label: "Volume (t)", value: "volumes" },
                { label: "Volume DU (t)", value: "volumesDtus" },
              ]}
              centerBlueText
              onChange={(e) => { volume.upDateValue(e.value); volume.upDateDefaultValue(e); }}
              {...volume}
            />
          </h4>
          <div className="mb-8 border-r shrink grow basis-1/2 md:p-8 md:mb-0">
            <BarChart
              data={dataVolume}
              escolha={volume.initValue}
              filters={filters}
              items={dataVolume.groups}
              title="Volume Médio"
              tipo="barra"
              total={totalVolume}
              metaInfo="Meta de volume da carteira para o mês selecionado, por grupo de produto"
            />
          </div>
        </div>
        <div>
          <div className="max-w-[400px] mx-auto">
            <CustomSelect
              placeholder="Faturamento (R$)"
              name="faturamento"
              className="select-filter focus:outline-none focus:border-none focus:shadow-none"
              options={[
                { label: "Faturamento (R$)", value: "revenues" },
                { label: "Fob. Líquido (R$/t)", value: "liquidos" },
              ]}
              centerBlueText
              onChange={(e) => { faturamento.upDateValue(e.value); faturamento.upDateDefaultValue(e); }}
              {...faturamento}
            />
          </div>
          <div className="shrink grow basis-1/2 md:p-8 relative top-[-1.1rem]">
            <LineChart data={dataFaturamento} escolha={faturamento.initValue} />
            <GraficoClienteLegenda
              items={dataFaturamento.groups}
              filters={filters}
              title="Faturamento Médio:"
              tipo="linha"
              escolha={faturamento.initValue}
              total={totalFaturamento}
            />
          </div>
        </div>
      </div>
      {faturamentoLastCreatedAtDate && (
        <p className="mt-2 flex justify-center">
          Atualizado em: <strong className="ml-2">{faturamentoLastCreatedAtDate}</strong>
        </p>
      )}
    </div>
  );
};

export default GraficoClienteStatic;
